import React, { useState } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const ChartMakerGame = ({ gameData, onComplete }) => {
    const [currentChartIndex, setCurrentChartIndex] = useState(0);
    const [userAnswer, setUserAnswer] = useState('');
    const [feedback, setFeedback] = useState(null);
    const [score, setScore] = useState(0);

    const currentChart = gameData?.charts?.[currentChartIndex];

    const handleKeyPress = (val) => {
        if (feedback) return;
        triggerHaptic(20);
        if (val === 'C') {
            setUserAnswer('');
        } else {
            setUserAnswer(prev => prev + val);
        }
    };

    const checkAnswer = (selectedAnswer = null) => {
        const finalUserAnswer = selectedAnswer !== null ? selectedAnswer : userAnswer;
        if (!finalUserAnswer && selectedAnswer === null) return;
        
        const isNumeric = !isNaN(parseFloat(currentChart.correct_answer));
        let isCorrect = false;

        if (isNumeric) {
            isCorrect = parseFloat(finalUserAnswer) === parseFloat(currentChart.correct_answer);
        } else {
            isCorrect = String(finalUserAnswer).toLowerCase().trim() === String(currentChart.correct_answer).toLowerCase().trim();
        }

        if (isCorrect) {
            setFeedback('correct');
            const points = gameData.points_per_chart || 25;
            const newScore = score + points;
            setScore(newScore);
            triggerHaptic([100, 50, 100]);

            setTimeout(() => {
                if (currentChartIndex + 1 >= gameData.charts.length) {
                    onComplete?.(newScore);
                } else {
                    setCurrentChartIndex(prev => prev + 1);
                    setUserAnswer('');
                    setFeedback(null);
                }
            }, 3000);
        } else {
            setFeedback('wrong');
            triggerHaptic([50, 50, 50]);
            setTimeout(() => setFeedback(null), 2000);
        }
    };

    if (!gameData || !currentChart) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-blue-100 animate-pulse bg-white">
                <div className="text-6xl md:text-[100px] mb-8">📊</div>
                <h3 className="text-base md:text-3xl font-black text-blue-900 tracking-tighter uppercase">Generuojami duomenys...</h3>
            </div>
        );
    }

    const maxValue = Math.max(...currentChart.data.map(d => d.value));
    const isAnswerNumeric = !isNaN(parseFloat(currentChart.correct_answer));
    const progress = (currentChartIndex / gameData.charts.length) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-blue-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-blue-100 rounded-xl md:rounded-2xl flex items-center justify-center text-xl md:text-3xl shadow-inner animate-float">
                        📊
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                            Duomenų Analitikas
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Įžvalga</p>
                        <p className="text-sm md:text-2xl font-black text-blue-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center min-w-[80px] md:min-w-[100px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Grafikas</p>
                        <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{currentChartIndex + 1}/{gameData.charts.length}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-6 w-full bg-slate-200/50 rounded-full h-2 md:h-3 p-0.5 md:p-1 shadow-inner overflow-hidden border border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-blue-400 via-indigo-500 to-emerald-500 rounded-full transition-all duration-1000 shadow-[0_0_15px_rgba(59,130,246,0.3)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-8 py-4 md:py-8">
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-6 md:p-12 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center">
                <div className="absolute top-0 right-0 p-4 md:p-12 text-6xl md:text-[200px] opacity-[0.03] select-none pointer-events-none">📉</div>
                <div className="w-full space-y-4 md:space-y-12 relative z-10">
                    <div className="text-center space-y-2">
                        <h3 className="text-lg md:text-4xl font-black text-slate-800 tracking-tighter uppercase leading-none">{currentChart.title}</h3>
                        <p className="text-slate-400 font-bold tracking-widest uppercase text-[10px] md:text-xs">{currentChart.chart_type === 'bar' ? 'Stulpelinė diagrama' : 'Duomenų suvestinė'}</p>
                    </div>

                    <div className="flex items-end justify-around h-48 md:h-80 border-b-4 md:border-b-8 border-slate-200 px-2 md:px-10 pb-2">
                        {currentChart.data.map((item, index) => {
                            const heightPercent = (item.value / maxValue) * 90;
                            const colors = [
                                'from-rose-400 to-rose-600',
                                'from-blue-400 to-blue-600',
                                'from-emerald-400 to-emerald-600',
                                'from-amber-400 to-amber-600',
                                'from-purple-400 to-purple-600'
                            ];

                            return (
                                <div key={index} className="flex flex-col items-center flex-1 mx-1 md:mx-2 max-w-[120px] group">
                                    <div
                                        className={`w-full bg-gradient-to-t ${colors[index % colors.length]} rounded-t-xl md:rounded-t-3xl transition-all duration-1000 ease-out flex items-end justify-center pb-2 md:pb-4 shadow-xl relative`}
                                        style={{ height: `${heightPercent}%`, minHeight: '40px' }}
                                    >
                                        <div className="absolute -top-6 md:-top-10 text-2xl md:text-5xl animate-float" style={{ animationDelay: `${index * 0.2}s` }}>
                                            {item.emoji}
                                        </div>
                                        <span className="text-white text-[10px] md:text-xl font-black opacity-40">{item.value}</span>
                                    </div>
                                    <div className="mt-2 md:mt-4 text-center">
                                        <div className="text-[8px] md:text-sm font-black text-slate-600 tracking-tight leading-tight">{item.label}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );

    const targets = (
        <div className="space-y-4 md:space-y-8 flex flex-col items-center w-full max-w-4xl mx-auto py-4 md:py-8">
            <div className="premium-glass rounded-[2rem] md:rounded-[3.5rem] p-6 md:p-12 shadow-2xl border-white/60 bg-slate-900 relative overflow-hidden flex flex-col items-center w-full">
                <div className="absolute top-0 right-0 p-4 md:p-12 text-6xl md:text-8xl opacity-[0.05] select-none pointer-events-none text-white">❓</div>
                <div className="w-full space-y-4 md:space-y-10 relative z-10 text-center">
                    <h4 className="text-base md:text-4xl font-black text-white tracking-tighter leading-tight italic font-serif">
                        "{currentChart.question}"
                    </h4>

                    {isAnswerNumeric ? (
                        <div className="space-y-4 md:space-y-8 flex flex-col items-center w-full">
                            <div className="bg-slate-800 rounded-xl md:rounded-[2.5rem] p-4 md:p-8 border-2 md:border-4 border-slate-700 shadow-inner w-full max-w-xs md:max-w-sm">
                                <div className={`text-3xl md:text-6xl font-black font-mono tracking-widest transition-all ${feedback ? 'text-rose-400 animate-shake' : 'text-cyan-400'}`}>
                                    {userAnswer || '____'}
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-2 md:gap-4 w-full max-w-xs md:max-w-sm">
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 'C', 0, '✓'].map((btn) => (
                                    <button
                                        key={btn}
                                        onClick={() => btn === '✓' ? checkAnswer() : handleKeyPress(btn)}
                                        className={`
                                            h-14 md:h-20 rounded-xl md:rounded-[1.5rem] text-xl md:text-3xl font-black flex items-center justify-center transition-all duration-150 border-b-4 shadow-lg
                                            ${btn === '✓' ? 'bg-emerald-500 text-white border-emerald-700 active:bg-emerald-600' : 
                                              btn === 'C' ? 'bg-rose-500 text-white border-rose-700 active:bg-rose-600' :
                                              'bg-white text-slate-800 border-slate-200 active:translate-y-1 active:border-b-0'}
                                        `}
                                    >
                                        {btn}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-6 w-full p-2">
                            {currentChart.data.map((item, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => checkAnswer(item.label)}
                                    disabled={!!feedback}
                                    className="premium-button-3d p-4 md:p-8 bg-white border-b-4 md:border-b-8 border-slate-200 rounded-xl md:rounded-[2.5rem] text-sm md:text-2xl font-black text-slate-800 flex items-center justify-center gap-4 hover:scale-105 active:translate-y-1 active:border-b-0 transition-all disabled:opacity-40 shadow-xl"
                                >
                                    <span className="text-xl md:text-4xl">{item.emoji}</span>
                                    <span>{item.label}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className={`
                        max-w-xl w-full p-6 md:p-12 rounded-[2.5rem] md:rounded-[4rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px]
                        ${feedback === 'correct' ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 bg-white rounded-full flex items-center justify-center text-5xl shadow-2xl border-4 border-inherit animate-bounce">
                            {feedback === 'correct' ? '📈' : '📉'}
                        </div>
                        
                        <div className="text-center space-y-4 pt-4">
                            <h2 className={`text-2xl md:text-5xl font-black uppercase tracking-tighter ${feedback === 'correct' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {feedback === 'correct' ? 'TIKSLI ĮŽVALGA!' : 'DUOMENŲ KLAIDA!'}
                            </h2>

                            <div className={`p-4 md:p-8 rounded-2xl border-2 italic font-serif leading-relaxed ${feedback === 'correct' ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                                <p className="text-sm md:text-xl font-bold text-slate-700 text-center leading-relaxed">
                                    {feedback === 'correct' ? 'Puikiai išanalizavai grafiką!' : `Oi! Teisingas atsakymas buvo: ${currentChart.correct_answer}.`}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ChartMakerGame;
